import { useCallback } from 'react';
import { useError } from '@/contexts/ErrorContext';
import { showErrorToast } from '@/components/error';
import { createAppError, type ErrorContext, type ErrorRecoveryOptions } from '@/types/error.types';

interface UseErrorHandlerOptions {
  /** Show errors as toasts instead of full-page */
  useToast?: boolean;
  
  /** Default recovery options */
  defaultRecovery?: ErrorRecoveryOptions;
}

/**
 * Hook for handling errors in components
 * Provides convenient methods for error handling
 */
export function useErrorHandler(options: UseErrorHandlerOptions = {}) {
  const { showError: showContextError, clearError } = useError();
  const { useToast = false, defaultRecovery } = options;

  /**
   * Handle an error with optional context and recovery options
   */
  const handleError = useCallback((
    error: unknown,
    context?: Partial<ErrorContext>,
    recovery?: ErrorRecoveryOptions
  ) => {
    const appError = createAppError(error, context);
    
    if (useToast) {
      showErrorToast(appError);
    } else {
      showContextError(error, context, recovery || defaultRecovery);
    }
  }, [useToast, showContextError, defaultRecovery]);

  /**
   * Handle API errors specifically
   * If error is already an AppError (from apiFetch), use it directly
   * Otherwise, wrap it with API context
   */
  const handleApiError = useCallback((
    error: unknown,
    endpoint?: string,
    method?: string
  ) => {
    // Check if it's already an AppError from apiFetch
    if (error && typeof error === 'object' && 'id' in error && 'category' in error) {
      // It's already formatted, just show it
      if (useToast) {
        showErrorToast(error as any);
      } else {
        showContextError(error);
      }
      return;
    }
    
    // Otherwise, wrap it with API context
    handleError(error, {
      category: 'network',
      severity: 'error',
      technicalDetails: {
        endpoint,
        method,
        timestamp: new Date().toISOString(),
        userAgent: window.navigator.userAgent,
        url: window.location.href,
      },
      suggestedActions: [
        'Check your internet connection',
        'Try again in a few moments',
        'Contact support if the issue persists',
      ],
    });
  }, [handleError, useToast, showContextError, showErrorToast]);

  /**
   * Handle validation errors
   */
  const handleValidationError = useCallback((
    message: string,
    fields?: Record<string, string[]>
  ) => {
    handleError(message, {
      category: 'validation',
      severity: 'warning',
      metadata: { fields },
      suggestedActions: [
        'Please check the form for errors',
        'Ensure all required fields are filled correctly',
      ],
    });
  }, [handleError]);

  /**
   * Wrap an async function with error handling
   */
  const withErrorHandling = useCallback(<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    context?: Partial<ErrorContext>
  ): T => {
    return (async (...args: Parameters<T>) => {
      try {
        return await fn(...args);
      } catch (error) {
        handleError(error, context);
        throw error; // Re-throw so caller can handle if needed
      }
    }) as T;
  }, [handleError]);

  /**
   * Create a retry handler
   */
  const createRetryHandler = useCallback((
    fn: () => Promise<void>,
    maxRetries: number = 3
  ) => {
    let retryCount = 0;

    const retry = async () => {
      try {
        await fn();
        clearError();
      } catch (error) {
        retryCount++;
        
        if (retryCount >= maxRetries) {
          handleError(error, {
            message: `Failed after ${maxRetries} attempts`,
            severity: 'error',
            recoverable: false,
          });
        } else {
          handleError(error, {
            message: `Attempt ${retryCount} of ${maxRetries} failed`,
            severity: 'warning',
            recoverable: true,
          }, {
            canRetry: true,
            onRetry: retry,
          });
        }
      }
    };

    return retry;
  }, [handleError, clearError]);

  return {
    handleError,
    handleApiError,
    handleValidationError,
    withErrorHandling,
    createRetryHandler,
    clearError,
  };
}
